<template>
    <div v-if="isLoaded">
        <q-select
            v-model="select"
            :options="loadedItems"
            :disable="disable"
            :loading="loading"
            :label="label"
            :rules="rules"
            :use-input="searchable ? true : false"
            @keydown="debounceSearch"
            @virtual-scroll="scroll"
            @popup-hide="handlePopupHide"
            :clearable="clearable"
            :dense="dense"
            :outlined="outlined"
            :emit-value="emitValue"
            :map-options="true"
            :multiple="multiple"
            @clear="emitClearEvent"
            class="bg-white"
            color="accent"
        >
            <template v-slot:after>
                <slot name="add-option"></slot>
            </template>
        </q-select>
    </div>
</template>

<script>
export default {
    name: 'RemoteSelect',
    emits: ['update:modelValue', 'clear'],
    props: {
        label: {
            type: String,
            required: true
        },
        baseURL: {
            type: String,
            required: true
        },
        urlParams: {
            type: Object,
            required: false
        },
        /**
         * The label name of the option
         */
        optionLabel: {
            type: String,
            required: false,
            default: 'name'
        },
        /**
         * The value name of the option
         */
        optionValue: {
            type: String,
            required: false,
            default: 'id'
        },
        rules: {
            type: Array,
            required: false
        },
        dense: {
            type: Boolean,
            required: false,
            default() {
                return false;
            }
        },
        disable: {
            type: Boolean,
            required: false,
            default() {
                return false;
            }
        },
        clearable: {
            type: Boolean,
            required: false,
            default() {
                return false;
            }
        },
        searchable: {
            type: Boolean,
            required: false,
            default() {
                return false;
            }
        },
        outlined: {
            type: Boolean,
            required: false,
            default() {
                return false;
            }
        },
        modelValue: {
            type: [Number, String, Object, Array],
            required: false,
            default() {
                return null;
            }
        },
        /**
         * When emitting value, emit the entire option object.
         */
        isEmitOption: {
            type: Boolean,
            required: false,
            default: false
        },
        multiple: {
            type: Boolean,
            required: false,
            default: false
        },
        emitValue: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            select: null,
            loadedItems: [], // loaded items from server
            nextPage: null, // next url to load when scrolling down
            loading: false,
            currentSearch: '',
            debounce: null
        };
    },
    async mounted() {
        if (this.loadedItems.length < 1) {
            // initialize the list of items
            await this.initRemoteSelect();
        }
    },
    watch: {
        modelValue: {
            immediate: true,
            async handler(value) {
                // update selection
                this.select = value;
            }
        },
        select: {
            async handler(value) {
                // emit new value bound for this remote-select's v-model
                this.$emit('update:modelValue', value);
            }
        },
        /**
         * Reinitialize the component is the computed url changes
         */
        async url(value) {
            await this.initRemoteSelect();
        }
    },
    computed: {
        /**
         * Check if the loaded items are available.
         * @return {Boolean}
         */
        isLoaded() {
            return this.loadedItems.length > 0;
        },
        /**
         * Url to fetch data from. It combines the baseUrl with url parameters
         * @return {String}
         */
        url() {
            if (!this.baseURL) {
                // if the base url does not exist, do nothing
                return null;
            }
            // get fields
            const params = { ...this.urlParams };
            // include search in the params
            if (this.currentSearch) {
                params['search'] = this.currentSearch;
            }
            const fields = Object.keys(params);
            // build query string in URL
            const queries = [];
            fields.forEach(field => {
                if (params[field] !== '') {
                    // add query
                    queries.push(`${field}=${params[field]}`);
                }
            });
            // join queries
            let url = this.baseURL;
            if (queries.length > 0) {
                url += `?${queries.join('&')}`;
            }
            return url;
        }
    },
    methods: {
        /**
         * Init the remote select and get the first page of the options
         */
        async initRemoteSelect() {
            if (this.url) {
                // start loading
                this.loading = true;
                this.nextPage = null;
                await this.loadItems(this.url)
                    .then(response => {
                        // reset the loaded item list
                        this.loadedItems = [];
                        const results = response.data.results;
                        /**
                         * The select component will disappear if the loadedItems is empty.
                         * The only way to show the select component is to have at least one item in the loadedItems.
                         * This is a workaround for the case when the server returns an empty array.
                         * More details: https://github.com/quasarframework/quasar/issues/8755
                         */
                        if (results.length > 0) {
                            // change format and assign to loaded item list
                            results.forEach(result => {
                                const option = {
                                    label: result[this.optionLabel]
                                };
                                // determine value to bound for option
                                if (this.isEmitOption) {
                                    option['value'] = JSON.parse(
                                        JSON.stringify(result)
                                    );
                                } else {
                                    option['value'] = result[this.optionValue];
                                }
                                this.loadedItems.push(option);
                            });
                        } else {
                            // no available options
                            this.loadedItems = [
                                {
                                    label: this.$t('No available options')
                                }
                            ];
                        }
                        // get the url of the next page
                        this.nextPage = response.data.next;
                    })
                    .catch(error => {
                        console.error(error);
                    })
                    .finally(() => {
                        // stop loading
                        this.loading = false;
                    });
            }
        },
        /**
         * Virtual scroll behavior
         * When the cursor scrolls to the bottom of the dropdown
         * fetch more options from server and push them to loaded item list
         * then update the next page number
         *
         * @param {Number} index - The index of the last item that was scrolled into view.
         * @param {Object} ref - Vue reference to the QVirtualList that triggered the event.
         */
        async scroll({ index, ref }) {
            // calculate the last index of the current option list
            const loadingIndex = this.loadedItems.length - 15;
            // check if there are more available options
            if (
                this.loading !== true &&
                this.nextPage !== null &&
                index >= loadingIndex
            ) {
                this.loading = true;
                await this.loadItems(this.nextPage)
                    .then(response => {
                        const results = response.data.results;
                        // change format and assign to loaded item list
                        results.forEach(result => {
                            const option = {
                                label: result[this.optionLabel]
                            };
                            // determine value to bound for option
                            if (this.isEmitOption) {
                                option['value'] = JSON.parse(
                                    JSON.stringify(result)
                                );
                            } else {
                                option['value'] = result[this.optionValue];
                            }
                            this.loadedItems.push(option);
                        });
                        this.nextPage = response.data.next;
                        this.$nextTick(() => {
                            ref.refresh();
                        });
                    })
                    .catch(error => {
                        console.error(error);
                    })
                    .finally(() => {
                        // stop loading
                        this.loading = false;
                    });
            }
        },
        /**
         * Load options from server
         * @return {Object} - response
         * @return {Object} - error
         */
        async loadItems(url) {
            return new Promise((resolve, reject) => {
                this.$axios
                    .get(url)
                    .then(response => {
                        resolve(response);
                    })
                    .catch(error => {
                        reject(error);
                    });
            });
        },
        /**
         * Set up search function with a debounce time
         */
        async debounceSearch(event) {
            clearTimeout(this.debounce);
            this.debounce = setTimeout(async () => {
                // set the search content to user input
                this.currentSearch = event.target.value;
            }, 300);
        },
        /**
         * Reset search results when dropdown/option selector is closed.
         */
        async handlePopupHide() {
            // reset the search query
            this.currentSearch = '';
        },
        /**
         * Emit clear event.
         * @param {any} value - Value.
         */
        emitClearEvent(value) {
            this.$emit('clear', value);
        }
    }
};
</script>

<style lang="scss" scoped></style>
